/*
 * Daarzood — storefront interactions.
 *
 * Everything here replaces a piece of React state from the original app:
 *   announcement rotator  <- AnnouncementBar's setInterval
 *   mobile menu           <- Header's menuOpen
 *   discover dropdown     <- Header's discHover
 *   predictive search     <- Header's debounced searchProducts()
 *   product gallery       <- ProductGallery's idx
 *   variant selection     <- ProductPage's selectedId
 *   quantity stepper      <- ProductPage's qty
 *   tabs                  <- ProductTabsCard
 *   collection sort       <- CollectionPage's sort select
 *
 * Plain DOM, no framework, no build step beyond Tailwind.
 */
(function () {
  'use strict';

  // Named DZ, not `root`: several init functions below take a DOM element
  // parameter called `root`, which would shadow it.
  var DZ = (window.Daarzood = window.Daarzood || {});
  var cfg = DZ;

  /* iOS Safari ignores overflow:hidden on <body>: the page keeps scrolling
     behind a drawer and the position is lost on close. Pinning the body with
     position:fixed and a negative offset is the only reliable fix, so the
     scroll position has to be stored and restored by hand. Reference counted
     because the drawer and the mobile menu can both be open. */
  DZ.scrollLock = DZ.scrollLock || (function () {
    var depth = 0;
    var savedY = 0;

    function apply() {
      savedY = window.scrollY || window.pageYOffset || 0;
      document.body.style.top = -savedY + 'px';
      document.body.classList.add('dz-locked');
    }

    function release() {
      document.body.classList.remove('dz-locked');
      document.body.style.top = '';
      window.scrollTo(0, savedY);
    }

    return {
      lock: function () { if (++depth === 1) apply(); },
      unlock: function () { if (depth > 0 && --depth === 0) release(); },
      reset: function () { if (depth > 0) { depth = 0; release(); } }
    };
  })();

  function onReady(fn) {
    if (document.readyState !== 'loading') fn();
    else document.addEventListener('DOMContentLoaded', fn);
  }

  /* boot() re-runs on every shopify:section:load and :select in the theme
     editor. Without this guard each pass stacks another set of listeners on
     elements that were never replaced, and another announcement interval. */
  /* Shared focus trap. cart.js traps the drawer; the mobile menu needs the same
     treatment or Tab walks out of the panel into the page behind it. */
  function focusables(container) {
    return Array.prototype.filter.call(
      container.querySelectorAll('a[href], button:not([disabled]), input:not([disabled]), textarea, select, [tabindex]:not([tabindex="-1"])'),
      function (el) { return el.offsetParent !== null; }
    );
  }

  function trapTab(e, container) {
    if (e.key !== 'Tab') return;
    var items = focusables(container);
    if (!items.length) return;
    var first = items[0];
    var last = items[items.length - 1];
    if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
    else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
  }

  function claim(el, key) {
    var attr = 'data-dz-' + key;
    if (el.hasAttribute(attr)) return false;
    el.setAttribute(attr, '1');
    return true;
  }

  /* ---------------- Announcement rotator (mobile) ---------------- */

  function initAnnouncement(root) {
    var slides = root.querySelectorAll('[data-announcement-slide]');
    if (slides.length < 2) return;

    var interval = parseInt(root.getAttribute('data-interval'), 10) || 5000;
    var i = 0;

    var timer = setInterval(function () {
      // .hidden loses to .inline-flex in the compiled sheet, so the slide
      // carries its own state class instead.
      slides[i].classList.remove('is-active');
      i = (i + 1) % slides.length;
      slides[i].classList.add('is-active');
    }, interval);
    root.setAttribute('data-dz-interval', timer);
  }

  /* ---------------- Mobile menu ---------------- */

  function initMobileMenu() {
    var panel = document.querySelector('[data-menu-panel]');
    if (!panel) return;

    var lastFocused = null;

    function open() {
      if (!panel.classList.contains('hidden')) return;
      lastFocused = document.activeElement;
      panel.classList.remove('hidden');
      DZ.scrollLock.lock();
      document.querySelectorAll('[data-menu-open]').forEach(function (b) {
        b.setAttribute('aria-expanded', 'true');
      });
      var first = panel.querySelector('[data-menu-close]');
      if (first) first.focus();
    }

    function close() {
      if (panel.classList.contains('hidden')) return;
      panel.classList.add('hidden');
      DZ.scrollLock.unlock();
      document.querySelectorAll('[data-menu-open]').forEach(function (b) {
        b.setAttribute('aria-expanded', 'false');
      });
      if (lastFocused && lastFocused.focus) lastFocused.focus();
      lastFocused = null;
    }

    document.addEventListener('click', function (e) {
      if (e.target.closest('[data-menu-open]')) { e.preventDefault(); open(); }
      else if (e.target.closest('[data-menu-close]')) { e.preventDefault(); close(); }
    });

    document.addEventListener('keydown', function (e) {
      if (panel.classList.contains('hidden')) return;
      if (e.key === 'Escape') { close(); return; }
      trapTab(e, panel);
    });
  }

  /* ---------------- Discover dropdown ---------------- */

  function initDropdown(wrap) {
    {
      var trigger = wrap.querySelector('[data-dropdown-trigger]');
      var panel = wrap.querySelector('[data-dropdown-panel]');
      if (!trigger || !panel) return;

      function show() { panel.classList.remove('hidden'); trigger.setAttribute('aria-expanded', 'true'); }
      function hide() { panel.classList.add('hidden'); trigger.setAttribute('aria-expanded', 'false'); }

      // Hover matched the React version; click and focus are added so the menu
      // is reachable by keyboard and on touch, which the original was not.
      wrap.addEventListener('mouseenter', show);
      wrap.addEventListener('mouseleave', hide);
      trigger.addEventListener('click', function (e) {
        e.preventDefault();
        panel.classList.contains('hidden') ? show() : hide();
      });
      wrap.addEventListener('focusout', function (e) {
        if (!wrap.contains(e.relatedTarget)) hide();
      });
      document.addEventListener('keydown', function (e) { if (e.key === 'Escape') hide(); });
    }
  }

  /* ---------------- Predictive search ---------------- */

  function initPredictiveSearch(root) {
    var input = root.querySelector('[data-predictive-input]');
    var results = root.querySelector('[data-predictive-results]');
    if (!input || !results) return;

    var timer = null;
    var controller = null;

    function hide() { results.classList.add('hidden'); results.innerHTML = ''; }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(timer);
      if (controller) controller.abort();

      if (!q) { hide(); return; }

      // 120ms debounce, matching the React effect.
      timer = setTimeout(function () {
        controller = new AbortController();
        var types = ['product'];
        if (cfg.searchShowCollections) types.push('collection');
        if (cfg.searchShowArticles) types.push('article');

        var url = cfg.routes.predictive_search_url +
          '?q=' + encodeURIComponent(q) +
          '&resources[type]=' + types.join(',') +
          '&resources[limit]=' + (cfg.searchLimit || 5) +
          '&section_id=predictive-search';

        fetch(url, { signal: controller.signal })
          .then(function (r) { return r.text(); })
          .then(function (html) {
            var parsed = new DOMParser().parseFromString(html, 'text/html');
            var inner = parsed.querySelector('[data-predictive-inner]');
            results.innerHTML = inner ? inner.innerHTML : '';
            results.classList.remove('hidden');
          })
          .catch(function (err) {
            if (err.name === 'AbortError') return;
            // Don't fail silently — the shopper can still submit for full results.
            results.innerHTML =
              '<div class="px-4 py-6 text-center text-sm text-muted-foreground"></div>';
            results.firstChild.textContent = (cfg.strings && cfg.strings.searchError) || '';
            results.classList.remove('hidden');
          });
      }, 120);
    });

    input.addEventListener('focus', function () {
      if (input.value.trim() && results.innerHTML) results.classList.remove('hidden');
    });

    document.addEventListener('click', function (e) {
      if (!root.contains(e.target)) hide();
    });

    document.addEventListener('keydown', function (e) {
      if (e.key === 'Escape') hide();
    });
  }

  /* ---------------- Product gallery ---------------- */

  function initGallery(root) {
    var mains = root.querySelectorAll('[data-gallery-main]');
    var thumbs = root.querySelectorAll('[data-gallery-thumb]');
    if (mains.length === 0) return;

    var index = 0;

    function show(next) {
      index = (next + mains.length) % mains.length;
      mains.forEach(function (img, i) { img.classList.toggle('hidden', i !== index); });
      thumbs.forEach(function (btn, i) {
        btn.classList.toggle('ring-[color:var(--brand-purple)]', i === index);
        btn.classList.toggle('ring-transparent', i !== index);
      });
    }

    thumbs.forEach(function (btn) {
      btn.addEventListener('click', function () {
        show(parseInt(btn.getAttribute('data-index'), 10));
      });
    });

    // The variant picker asks the gallery to move by event rather than by
    // clicking a thumbnail, because thumbnails are an optional setting.
    // The picker lives above [data-gallery] and events only travel upward, so
    // the listener has to sit on the shared ancestor it dispatches from.
    var scope = root.closest('[data-product-root]') || root;
    scope.addEventListener('dz:gallery:show', function (e) {
      var n = parseInt(e.detail, 10);
      if (isFinite(n)) show(n);
    });

    var prev = root.querySelector('[data-gallery-prev]');
    var next = root.querySelector('[data-gallery-next]');
    if (prev) prev.addEventListener('click', function () { show(index - 1); });
    if (next) next.addEventListener('click', function () { show(index + 1); });

    // Zoom: a real overlay rather than a new tab. Opening a tab loses the page,
    // the shopper's scroll position and the variant they had selected.
    var zoom = root.querySelector('[data-gallery-zoom]');
    if (zoom) {
      var overlay = null;
      var lastFocus = null;

      // theme.js has no element helper of its own; cart.js keeps its own copy.
      function node(tag, cls) {
        var n = document.createElement(tag);
        if (cls) n.className = cls;
        return n;
      }

      function closeZoom() {
        if (!overlay) return;
        overlay.remove();
        overlay = null;
        DZ.scrollLock.unlock();
        if (lastFocus) lastFocus.focus();
      }

      function openZoom() {
        var img = mains[index];
        if (!img) return;
        lastFocus = document.activeElement;

        overlay = node('div', 'dz-zoom');
        overlay.setAttribute('role', 'dialog');
        overlay.setAttribute('aria-modal', 'true');
        overlay.setAttribute('aria-label', img.alt || '');

        // Ask Shopify for a larger rendition than the one on the page; the src
        // is a CDN URL whose width parameter we can raise.
        var big = (img.currentSrc || img.src || '').replace(/([?&])width=\d+/, '$1width=1800');
        var full = node('img', 'dz-zoom__img');
        full.src = big;
        full.alt = img.alt || '';
        full.decoding = 'async';

        var close = node('button', 'dz-zoom__close');
        close.type = 'button';
        close.setAttribute('aria-label', (cfg.strings && cfg.strings.dismiss) || 'Close');
        close.innerHTML = '&times;';
        close.addEventListener('click', closeZoom);

        // Clicking the backdrop closes; clicking the image itself must not.
        overlay.addEventListener('click', function (e) {
          if (e.target === overlay) closeZoom();
        });
        full.addEventListener('click', function () {
          full.classList.toggle('is-zoomed');
        });

        overlay.appendChild(close);
        overlay.appendChild(full);
        document.body.appendChild(overlay);
        DZ.scrollLock.lock();
        close.focus();
      }

      zoom.addEventListener('click', openZoom);
      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && overlay) closeZoom();
      });
    }
  }

  /* ---------------- Product form: variants + quantity ---------------- */

  function escapeHtml(value) {
    return String(value).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }

  function initProduct(root) {
    var form = document.getElementById('ProductForm');
    if (!form) return;

    /* Quantity stepper */
    var qtyInput = root.querySelector('[data-qty-input]');
    if (qtyInput) {
      var down = root.querySelector('[data-qty-down]');
      var up = root.querySelector('[data-qty-up]');
      if (down) down.addEventListener('click', function () {
        qtyInput.value = Math.max(1, (parseInt(qtyInput.value, 10) || 1) - 1);
      });
      if (up) up.addEventListener('click', function () {
        qtyInput.value = (parseInt(qtyInput.value, 10) || 1) + 1;
      });
      qtyInput.addEventListener('change', function () {
        var n = parseInt(qtyInput.value, 10);
        qtyInput.value = isFinite(n) && n > 0 ? n : 1;
      });
    }

    /* Variant selection */
    var radios = root.querySelectorAll('[data-variant-radio]');
    var picker = root.querySelector('[data-variant-select]');
    if (radios.length === 0 && !picker) return;

    var idInput = root.querySelector('[data-variant-id]');
    var priceTarget = root.querySelector('[data-price-target]');
    var stickyPrice = root.querySelector('[data-sticky-price]');
    var addButton = root.querySelector('[data-add-to-cart]');
    var addText = root.querySelector('[data-add-to-cart-text]');
    var stickyAdd = root.querySelector('[data-sticky-add]');
    var availability = root.querySelector('[data-availability]');
    var availTitle = root.querySelector('[data-availability-title]');
    var availSub = root.querySelector('[data-availability-sub]');
    var skuTarget = root.querySelector('[data-variant-sku]');
    var barcodeTarget = root.querySelector('[data-variant-barcode]');
    var weightTarget = root.querySelector('[data-variant-weight]');
    var weightTarget = root.querySelector('[data-variant-weight]');

    var t = (cfg.strings || {});
    var fmt = (DZ.cart && DZ.cart.formatMoney) || function (c) { return c; };

    function select(radio) {
      var price = parseInt(radio.getAttribute('data-price'), 10);
      var compareAt = parseInt(radio.getAttribute('data-compare-at'), 10);
      var available = radio.getAttribute('data-available') === 'true';

      if (idInput) idInput.value = radio.value;

      // Keep the URL in step so a refresh or a shared link keeps the choice.
      var url = new URL(window.location.href);
      url.searchParams.set('variant', radio.value);
      window.history.replaceState({}, '', url);

      // Shopify rendered these with the money filter, so they already carry the
      // shop's format, the active market and the customer's currency. Rebuilding
      // them here from a cents value is how the currency went missing.
      var priceText = radio.getAttribute('data-price-money') || fmt(price);
      var compareText = radio.getAttribute('data-compare-money') || '';

      if (priceTarget) {
        var html = '<div class="flex flex-col"><div class="mt-4 flex items-baseline gap-3">' +
          '<span class="text-[28px] font-semibold tabular-nums text-foreground md:text-[30px]">' + escapeHtml(priceText) + '</span>';
        if (compareText && compareAt && compareAt > price) {
          var off = 100 - Math.round((price * 100) / compareAt);
          html += '<s class="text-base text-muted-foreground line-through tabular-nums">' + escapeHtml(compareText) + '</s>' +
            '<span class="rounded-md bg-[color:var(--sage-soft)] px-1.5 py-0.5 text-[11px] font-semibold text-[color:var(--sage)]" dir="ltr">&minus;' + off + '%</span>';
        }
        html += '</div></div>';
        priceTarget.innerHTML = html;
      }

      if (stickyPrice) stickyPrice.textContent = priceText;

      [addButton, stickyAdd].forEach(function (btn) {
        if (btn) btn.toggleAttribute('disabled', !available);
      });
      if (addText) addText.textContent = available ? t.addToCart : t.soldOut;

      if (availability) {
        availability.classList.toggle('bg-[color:var(--sage-soft)]', available);
        availability.classList.toggle('text-[color:var(--sage)]', available);
        availability.classList.toggle('bg-muted', !available);
        availability.classList.toggle('text-muted-foreground', !available);
      }
      if (availTitle) availTitle.textContent = available ? t.inStock : t.soldOut;
      if (availSub) availSub.textContent = available ? t.readyForDelivery : t.chooseAnother;

      // SKU and barcode vary by variant. Hide the row's entry rather than
      // leaving the previous variant's code sitting under a new price.
      if (skuTarget) {
        var sku = radio.getAttribute('data-sku') || '';
        skuTarget.textContent = sku;
        if (skuTarget.parentNode) skuTarget.parentNode.hidden = !sku;
      }
      if (barcodeTarget) {
        var barcode = radio.getAttribute('data-barcode') || '';
        barcodeTarget.textContent = barcode;
        barcodeTarget.hidden = !barcode;
      }

      // Pack weight varies by variant like everything else here; leaving the
      // previous one on screen made the picker look like it had not worked.
      if (weightTarget) {
        var weight = radio.getAttribute('data-weight') || '';
        weightTarget.textContent = weight
          ? String((t.approxWeight || '')).replace('[weight]', weight)
          : '';
        weightTarget.hidden = !weight;
      }

      // Pack weight varies by variant like everything else here; leaving the
      // previous one on screen made the picker look like it had not worked.
      if (weightTarget) {
        var weight = radio.getAttribute('data-weight') || '';
        weightTarget.textContent = weight ? String(t.approxWeight || '').replace('[weight]', weight) : '';
        weightTarget.hidden = !weight;
      }

      var mediaIndex = radio.getAttribute('data-media-index');
      if (mediaIndex !== null) {
        root.dispatchEvent(new CustomEvent('dz:gallery:show', { detail: mediaIndex }));
      }
    }

    radios.forEach(function (radio) {
      radio.addEventListener('change', function () { if (radio.checked) select(radio); });
    });

    // The dropdown picker reuses select(): an <option> exposes the same value
    // and data-* attributes a radio does, so nothing downstream needs to know
    // which control the merchant chose.
    if (picker) {
      picker.addEventListener('change', function () {
        var opt = picker.options[picker.selectedIndex];
        if (opt) select(opt);
      });
    }
  }

  /* ---------------- Tabs ---------------- */

  function initTabs(root) {
    var triggers = Array.prototype.slice.call(root.querySelectorAll('[data-tab-trigger]'));
    if (triggers.length === 0) return;

    function select(trigger, focus) {
      var name = trigger.getAttribute('data-tab-trigger');
      triggers.forEach(function (t) {
        var on = t === trigger;
        t.setAttribute('aria-selected', String(on));
        // Roving tabindex: only the selected tab is a tab stop.
        t.setAttribute('tabindex', on ? '0' : '-1');
      });
      root.querySelectorAll('[data-tab-panel]').forEach(function (panel) {
        panel.hidden = panel.getAttribute('data-tab-panel') !== name;
      });
      if (focus) trigger.focus();
    }

    triggers.forEach(function (trigger, i) {
      trigger.addEventListener('click', function () { select(trigger); });

      trigger.addEventListener('keydown', function (e) {
        // In RTL the horizontal arrows are visually mirrored.
        var rtl = document.documentElement.getAttribute('dir') === 'rtl';
        var next = rtl ? 'ArrowLeft' : 'ArrowRight';
        var prev = rtl ? 'ArrowRight' : 'ArrowLeft';
        var target = null;

        if (e.key === next) target = triggers[(i + 1) % triggers.length];
        else if (e.key === prev) target = triggers[(i - 1 + triggers.length) % triggers.length];
        else if (e.key === 'Home') target = triggers[0];
        else if (e.key === 'End') target = triggers[triggers.length - 1];

        if (target) { e.preventDefault(); select(target, true); }
      });
    });

    var current = triggers.filter(function (t) { return t.getAttribute('aria-selected') === 'true'; })[0];
    select(current || triggers[0]);
  }

  /* ---------------- Collection sort ---------------- */

  function initAutoSubmit(select) {
    select.addEventListener('change', function () {
      var form = select.closest('form');
      if (form) form.submit();
    });
  }

  /* ---------------- Product recommendations ---------------- */

  function initRecommendations(root) {
    var url = root.getAttribute('data-url');
    if (!url) return;

    fetch(url)
      .then(function (r) { return r.text(); })
      .then(function (html) {
        var parsed = new DOMParser().parseFromString(html, 'text/html');
        var fresh = parsed.querySelector('[data-product-recommendations]');
        if (fresh && fresh.innerHTML.trim()) root.innerHTML = fresh.innerHTML;
      })
      .catch(function () { /* recommendations are non-critical */ });
  }

  /* ---------------- Address forms ----------------
     country_option_tags renders each country with a data-provinces payload.
     Shopify's own helper needs jQuery, so this populates the province select
     directly and hides it for countries that have none. */

  function initAddressForm(select) {
    var provinceId = select.getAttribute('data-province-id');
    var wrapId = select.getAttribute('data-province-container-id');
    var province = provinceId && document.getElementById(provinceId);
    var wrap = wrapId && document.getElementById(wrapId);
    if (!province) return;

    function fill() {
      var opt = select.options[select.selectedIndex];
      var raw = opt && opt.getAttribute('data-provinces');
      var list = [];
      try { list = raw ? JSON.parse(raw) : []; } catch (e) { list = []; }

      province.innerHTML = '';
      if (!list.length) {
        if (wrap) wrap.style.display = 'none';
        province.removeAttribute('required');
        return;
      }
      if (wrap) wrap.style.display = '';

      var want = province.getAttribute('data-default') || '';
      list.forEach(function (pair) {
        var o = document.createElement('option');
        o.value = pair[0];
        o.textContent = pair[1];
        if (pair[0] === want) o.selected = true;
        province.appendChild(o);
      });
    }

    // Restore the saved country before the first fill.
    var def = select.getAttribute('data-default');
    if (def) {
      for (var i = 0; i < select.options.length; i++) {
        if (select.options[i].value === def) { select.selectedIndex = i; break; }
      }
    }

    select.addEventListener('change', fill);
    fill();
  }

  /* ---------------- Media failure ----------------
     A missing image would otherwise render the browser's broken-image glyph
     inside an otherwise styled card. Swap it for the card's own surface. */

  function initMediaFallbacks() {
    document.addEventListener('error', function (e) {
      var img = e.target;
      if (!img || img.tagName !== 'IMG' || img.dataset.dzFailed) return;
      img.dataset.dzFailed = '1';
      img.style.visibility = 'hidden';
      var holder = img.parentElement;
      if (holder) holder.classList.add('dz-media-missing');
    }, true); // capture: image error events do not bubble
  }

  /* ---------------- Boot ---------------- */

  /* ---------------- Sticky header ----------------
     Modes come from theme settings: scroll_up retracts on the way down and
     returns on the way up; always pins; none leaves it in the flow. */
  function initHeader(header) {
    var mode = header.getAttribute('data-sticky') || 'scroll_up';
    if (mode === 'none') return;

    header.classList.add('dz-header--sticky');

    function measure() {
      document.documentElement.style.setProperty('--dz-header-height', header.offsetHeight + 'px');
    }
    measure();
    window.addEventListener('resize', measure);
    if (window.ResizeObserver) new ResizeObserver(measure).observe(header);

    if (mode !== 'scroll_up') return;

    var last = window.scrollY || 0;
    var ticking = false;
    // Ignore sub-pixel and momentum jitter, which otherwise flickers the header
    // on iOS rubber-band scrolling.
    var THRESHOLD = 6;

    function update() {
      ticking = false;
      var y = window.scrollY || 0;
      var height = header.offsetHeight;

      // Rubber-band overscroll on iOS reports negative offsets.
      if (y < 0) y = 0;

      if (header.getAttribute('data-shadow') !== 'false') {
        header.classList.toggle('dz-header--scrolled', y > 4);
      }

      var delta = y - last;
      if (Math.abs(delta) < THRESHOLD) return;

      if (y <= height) {
        header.classList.remove('dz-header--hidden');
      } else if (delta > 0) {
        header.classList.add('dz-header--hidden');
      } else {
        header.classList.remove('dz-header--hidden');
      }
      last = y;
    }

    window.addEventListener('scroll', function () {
      if (ticking) return;
      ticking = true;
      window.requestAnimationFrame(update);
    }, { passive: true });

    // A focused element inside the header (skip link, search) must never be
    // scrolled out of view.
    header.addEventListener('focusin', function () {
      header.classList.remove('dz-header--hidden');
    });
  }

  /* ---------------- Reveal on scroll ----------------
     Opt-in via theme settings, and skipped entirely when the visitor asks for
     reduced motion or IntersectionObserver is unavailable. */
  function initReveal() {
    if (!DZ.animationsReveal) return;
    if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) return;
    if (!('IntersectionObserver' in window)) return;

    var targets = document.querySelectorAll('.shopify-section > section, .shopify-section > div');
    if (!targets.length) return;

    var io = new IntersectionObserver(function (entries) {
      entries.forEach(function (entry) {
        if (!entry.isIntersecting) return;
        entry.target.classList.add('dz-revealed');
        io.unobserve(entry.target);
      });
    }, { rootMargin: '0px 0px -8% 0px', threshold: 0.05 });

    targets.forEach(function (el, i) {
      // The first screenful is already visible; revealing it causes a flash.
      if (i === 0 || el.getBoundingClientRect().top < window.innerHeight) {
        el.classList.add('dz-revealed');
        return;
      }
      el.classList.add('dz-reveal');
      io.observe(el);
    });
  }

  /* One failing initialiser must not take the rest of the page down. Each is
     isolated, and the failure is reported to the console rather than swallowed
     so it is still debuggable. */
  function guard(label, fn) {
    return function () {
      try { return fn.apply(null, arguments); }
      catch (err) {
        if (window.console && console.warn) console.warn('[daarzood] ' + label + ' failed:', err);
      }
    };
  }

  /* ---------------- Saved items ---------------- */

  function initWishlist(root) {
    var id = root.getAttribute('data-product-id');
    if (!id) return;
    var KEY = 'dz-saved';

    // localStorage throws in private windows and when site data is blocked, so
    // every read and write is guarded and the button simply does nothing.
    function read() {
      try { return JSON.parse(localStorage.getItem(KEY) || '[]'); }
      catch (e) { return []; }
    }
    function write(list) {
      try { localStorage.setItem(KEY, JSON.stringify(list)); } catch (e) {}
    }

    function paint(on) {
      root.setAttribute('aria-pressed', String(on));
      root.classList.toggle('is-saved', on);
    }

    paint(read().indexOf(id) > -1);

    root.addEventListener('click', function () {
      var list = read();
      var at = list.indexOf(id);
      if (at > -1) list.splice(at, 1); else list.push(id);
      write(list);
      paint(at === -1);
    });
  }

  /* ---------------- Same-day delivery countdown ---------------- */

  function initCountdown(root) {
    var hour = parseInt(root.getAttribute('data-cutoff-hour'), 10);
    if (!isFinite(hour)) return;
    var out = root.querySelector('[data-countdown-text]');
    var template = cfg.strings && cfg.strings.deliveryCountdown;
    if (!out || !template) return;

    // The static note is the fallback; hide it only once the countdown has a
    // real figure, so a passed cut-off leaves the page saying something true.
    var fallback = document.querySelector('[data-delivery-fallback]');

    function tick() {
      var now = new Date();
      var cutoff = new Date(now);
      cutoff.setHours(hour, 0, 0, 0);
      var ms = cutoff - now;

      if (ms <= 0) {
        root.hidden = true;
        if (fallback) fallback.hidden = false;
        return false;
      }

      var mins = Math.floor(ms / 60000);
      var h = Math.floor(mins / 60);
      var m = mins % 60;
      var time = h > 0 ? h + 'h ' + m + 'm' : m + 'm';
      out.textContent = String(template).replace('[time]', time);
      root.hidden = false;
      if (fallback) fallback.hidden = true;
      return true;
    }

    if (!tick()) return;
    // A minute is the smallest unit shown, so there is nothing to gain from
    // ticking faster than that.
    var id = setInterval(function () {
      if (!tick()) clearInterval(id);
    }, 60000);
    root.setAttribute('data-dz-interval', id);
  }

  /* ---------------- Frequently bought together ---------------- */

  function initBundle(root) {
    var checks = Array.prototype.slice.call(root.querySelectorAll('[data-bundle-check]'));
    if (checks.length === 0) return;

    var totalEl = root.querySelector('[data-bundle-total]');
    var savingsEl = root.querySelector('[data-bundle-savings]');
    var errorEl = root.querySelector('[data-bundle-error]');
    var button = root.querySelector('[data-bundle-add]');
    var fmt = (DZ.cart && DZ.cart.formatMoney) || function (c) { return c; };

    function selected() {
      return checks.filter(function (c) { return c.checked && !c.disabled; });
    }

    function recalc() {
      var picked = selected();
      var total = 0;
      var compare = 0;
      picked.forEach(function (c) {
        total += parseInt(c.getAttribute('data-price'), 10) || 0;
        compare += parseInt(c.getAttribute('data-compare-at'), 10) || 0;
      });

      if (totalEl) totalEl.textContent = picked.length ? fmt(total) : fmt(0);
      if (button) button.disabled = picked.length === 0;

      // Only claim a saving when there genuinely is one.
      if (savingsEl) {
        var saved = compare - total;
        if (saved > 0) {
          savingsEl.textContent = String(cfg.strings.bundleSavings || '').replace('[amount]', fmt(saved));
          savingsEl.hidden = false;
        } else {
          savingsEl.hidden = true;
        }
      }
    }

    checks.forEach(function (c) {
      c.addEventListener('change', recalc);
    });

    if (button) {
      button.addEventListener('click', function () {
        var picked = selected();
        if (picked.length === 0) return;
        if (errorEl) errorEl.hidden = true;

        var items = picked.map(function (c) {
          return { id: parseInt(c.getAttribute('data-variant-id'), 10), quantity: 1 };
        });

        if (!DZ.cart || !DZ.cart.addItems) return;
        DZ.cart.addItems(items, button).catch(function (err) {
          if (!errorEl) return;
          errorEl.textContent = (err && err.message) || cfg.strings.cartError || '';
          errorEl.hidden = false;
        });
      });
    }

    recalc();
  }
  function boot(scope) {
    var el = scope || document;
    function each(selector, fn, key) {
      var safe = guard(key, fn);
      el.querySelectorAll(selector).forEach(function (node) {
        if (claim(node, key)) safe(node);
      });
    }
    each('[data-header]', initHeader, 'header');
    each('[data-address-country-select]', initAddressForm, 'address');
    each('[data-announcement-rotator]', initAnnouncement, 'announce');
    each('[data-predictive-search]', initPredictiveSearch, 'search');
    each('[data-gallery]', initGallery, 'gallery');
    each('[data-product-root]', function (r) { initProduct(r); initTabs(r); }, 'product');
    // Tabs live in their own section now, outside [data-product-root], so bind
    // them wherever a tablist appears.
    each('[data-tabs]', function (r) { initTabs(r.closest('[data-tab-scope]') || r.parentNode); }, 'tabs');
    each('[data-product-recommendations]', initRecommendations, 'recs');
    each('[data-bundle]', initBundle, 'bundle');
    each('[data-delivery-countdown]', initCountdown, 'countdown');
    each('[data-wishlist]', initWishlist, 'wishlist');
    each('[data-wishlist-count]', function (badge) {
      var n = 0;
      try { n = (JSON.parse(localStorage.getItem('dz-saved') || '[]') || []).length; }
      catch (e) { n = 0; }
      badge.textContent = n;
      badge.hidden = n === 0;
    }, 'wishlistcount');
    each('[data-dropdown]', initDropdown, 'dropdown');
    each('[data-auto-submit]', initAutoSubmit, 'autosubmit');
  }

  document.addEventListener('shopify:section:unload', function (e) {
    e.target.querySelectorAll('[data-dz-interval]').forEach(function (node) {
      clearInterval(parseInt(node.getAttribute('data-dz-interval'), 10));
    });
  });

  onReady(function () {
    guard('mobile-menu', initMobileMenu)();
    guard('boot', boot)(document);
    guard('reveal', initReveal)();
    guard('media', initMediaFallbacks)();
  });

  // The theme editor swaps section DOM in place; re-bind what it replaced.
  document.addEventListener('shopify:section:load', function (e) { boot(e.target); });
  document.addEventListener('shopify:section:select', function (e) { boot(e.target); });
})();
